import { registerSW } from 'virtual:pwa-register';
import { quietToast } from './ui/toast';
import { str } from './ui/strings';

/** How often to ask the server whether a new build shipped. */
const CHECK_EVERY_MS = 60 * 60 * 1000;

/** How often a waiting update re-tries the safe-moment test. */
const RETRY_EVERY_MS = 5_000;

/**
 * Install the service worker and keep the installed app current.
 *
 * Wirespan is fully self-contained once cached — the grid is generated on
 * device, so an installed copy plays offline indefinitely, including the
 * archive. New builds are polled for in the background (hourly, and whenever
 * the app comes back into view — the left-in-the-app-switcher-overnight path)
 * and applied, which is a reload, at the first moment `safeToApply` says the
 * reload loses nothing: never mid-round, at latest on next launch (GDD §8).
 * What "safe" means is the app's call; this module only asks.
 */
export function registerServiceWorker(safeToApply: () => boolean): void {
  if (import.meta.env.DEV) return;

  // A new build downloaded and parked, wanting its moment. One flag, one
  // retry clock and one visibility listener serve however many updates a
  // page lifetime sees — re-arming per onNeedRefresh leaked listeners.
  let waiting = false;
  let applied = false;
  let retryTimer: ReturnType<typeof setInterval> | null = null;

  const tryApply = (): void => {
    if (!waiting || applied || !safeToApply()) return;
    applied = true;
    if (retryTimer) clearInterval(retryTimer);
    void updateSW();
  };
  document.addEventListener('visibilitychange', tryApply);

  const updateSW = registerSW({
    immediate: true,

    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      // Offline, there is nothing to fetch — and some browsers reject the
      // update() rather than skipping it.
      const check = (): void => {
        if (navigator.onLine) void registration.update().catch(() => undefined);
      };
      setInterval(check, CHECK_EVERY_MS);
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) check();
      });
    },

    // A new build is downloaded and waiting. Apply it the moment a reload
    // would reproduce what's on screen: now, on the next lull, or when the
    // page hides — a round in progress holds it, and a hold that never finds
    // its moment resolves on the next launch, where an untouched board is
    // unconditionally safe (safeToReload in src/app.ts; GDD §8's "at latest
    // on next launch").
    onNeedRefresh() {
      waiting = true;
      if (!retryTimer) retryTimer = setInterval(tryApply, RETRY_EVERY_MS);
      // A microtask, not a direct call: this hook can fire inside registerSW,
      // before `updateSW` above has a value to apply with.
      queueMicrotask(tryApply);
    },

    onOfflineReady() {
      // Ambient news yields: never stomp a nudge the player just provoked.
      quietToast(() => str().offlineReady);
    },
    onRegisterError(error) {
      console.error('Service worker registration failed', error);
    },
  });
  // A worker already waiting when registration ran may have asked before
  // updateSW existed to answer.
  tryApply();
}
